import React from 'react';
import Image from 'next/image';
import clsx from 'clsx';

import { Course, Video } from '@edx/graphql/generated';
import { formatDurationInSeconds } from '@edx/helpers/datetime';
import { Link } from '@edx/components/link';
import { Body, Caption, Title } from '@edx/components/typography';
import { IconAngleDown, IconPlaySolid } from '@edx/components/icons';

type Props = {
  item: Course;
  position: number;
  isExpanded: boolean;
  onToggleVisibility: () => void;
};

export const CoursePathItem: React.FC<Props> = ({ position, item, isExpanded, onToggleVisibility }) => (
  <div className="border-t border-gray-600 border-opacity-50" data-testid="feedItem">
    <div className="w-full flex flex-col">
      <div className="flex items-start md:items-center space-x-2">
        <div className="flex-shrink-0 mt-6 md:mt-0 w-10 md:w-12 h-10 md:h-12 bg-gray-800 border border-gray-600 rounded-full flex items-center justify-center">
          <Title tag="h4" size="small" className="text-white leading-none">
            {position}
          </Title>
        </div>

        <div className="group flex-grow py-6 px-2 flex flex-col md:flex-row space-x-0 md:space-x-4 space-y-2 md:space-y-0 hover:bg-opacity-20 hover:bg-gray-600">
          <Link href={item.resourcePath} className="relative bg-gray-800 w-full md:w-1/4 flex-shrink-0">
            <Image src={item.imageUrl as string} width={238} height={134} layout="responsive" />

            <div className="absolute inset-0 flex items-center justify-center p-2 bg-gray-800 bg-opacity-50 group-hover:bg-opacity-0 transition duration-150">
              <div className="flex">
                <div className="p-2 bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-all duration-200 ease transform translate-y-3 group-hover:translate-y-0">
                  <IconPlaySolid className="text-primary-500 w-6" />
                </div>
              </div>
            </div>

            <Body
              tag="span"
              size="small"
              variant="semibold"
              className="absolute z-10 right-0 bottom-0 px-2 py-1 bg-gray-800 text-gray-300 tracking-widest bg-opacity-80 group-hover:bg-opacity-0 transition-all duration-200 ease transform group-hover:translate-y-1 group-hover:text-transparent"
            >
              {formatDurationInSeconds(item.totalDuration)}
            </Body>

            {/* Overlay */}
            <div className="absolute inset-0 cursor-pointer flex flex-col items-center space-y-2 justify-center bg-gray-800 bg-opacity-40 group-hover:bg-opacity-0 transition duration-300" />
          </Link>

          <div
            className="w-full md:w-3/4 flex items-center justify-between cursor-pointer"
            onClick={onToggleVisibility}
          >
            <div className="flex flex-col justify-center">
              <div className="flex items-center justify-between">
                <Title tag="h4" className="text-gray-300">
                  {item.name}
                </Title>
              </div>

              <Caption tag="span" variant="semibold" className="text-gray-600 tracking-widest">
                {`${item.videosCount} videos`}
              </Caption>
            </div>

            <div
              className={clsx(
                'flex-shrink-0 w-10 h-10 bg-gray-800 flex items-center justify-center rounded-full focus:outline-none transition-all duration-200',
                { 'transform rotate-180': isExpanded }
              )}
              aria-expanded={isExpanded}
              aria-label="Mostrar videos"
            >
              <IconAngleDown className="w-6 h-6 text-gray-300" />
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <ul className="ml-12 md:ml-14">
          {(item.videos as Video[]).map((video: Video) => (
            <li key={video.resourcePath}>
              <Link
                href={video.resourcePath}
                className="group flex-grow py-6 px-2 flex flex-col md:flex-row space-x-0 md:space-x-4 space-y-2 md:space-y-0 hover:bg-opacity-20 hover:bg-gray-600"
              >
                <div className="relative bg-gray-800 w-full md:w-1/4 flex-shrink-0">
                  <Image src={video.imageUrl as string} width={238} height={134} layout="responsive" />

                  <div className="absolute inset-0 flex items-center justify-center p-2 bg-gray-800 bg-opacity-50 group-hover:bg-opacity-0 transition duration-150">
                    <div className="flex">
                      <div className="p-2 bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-all duration-200 ease transform translate-y-3 group-hover:translate-y-0">
                        <IconPlaySolid className="text-primary-500 w-6" />
                      </div>
                    </div>
                  </div>

                  <Body
                    tag="span"
                    size="small"
                    variant="semibold"
                    className="absolute z-10 right-0 bottom-0 px-2 py-1 bg-gray-800 text-gray-300 tracking-widest bg-opacity-80 group-hover:bg-opacity-0 transition-all duration-200 ease transform group-hover:translate-y-1 group-hover:text-transparent"
                  >
                    {formatDurationInSeconds(video.duration)}
                  </Body>

                  {/* Overlay */}
                  <div className="absolute inset-0 cursor-pointer flex flex-col items-center space-y-2 justify-center bg-gray-800 bg-opacity-40 group-hover:bg-opacity-0 transition duration-300" />
                </div>

                <div className="w-full md:w-3/4">
                  <Body tag="h4" size="xlarge" className="text-gray-300">
                    {video.name}
                  </Body>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
);
